import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonView } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  lessonTitle: string
  planbookId: string
  courseId: string
  websiteId?: string
  useStudentVersion: boolean
}

export default function lessonViewAction(payload: Payload): PreparedAction {
  return {
    name: "LESSON_VIEW",
    payload: payload,
    patches: null,
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let primaryPossessive = getPossessiveForPlanbook(findFn, userId, models.planbook?.id)
  let context = defaultLessonContext(payload, models)
  let newContext = assign({}, context, { classWebsiteId: payload.websiteId })
  let primaryObjectSpecifier
  if (payload.useStudentVersion) {
    primaryObjectSpecifier = "student_version"
  } else {
    primaryObjectSpecifier = "full_version"
  }
  let newEvent: LessonView = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,

    primary_verb: "view",
    primary_possessive: primaryPossessive,
    primary_object_type: "lesson",
    primary_object_specifier: primaryObjectSpecifier,
  }

  return {
    title: "Viewing a Lesson",
    titleForAnalytics: null,
    description: `Viewing the ${payload.lessonTitle} lesson from ${models.course?.attributes.title}`,
    activity: defaultLessonActivity(payload, models),
    context: newContext,
    newAnalyticsEvents: [newEvent],
    isQuiet: true,
  }
}
